import * as mediaQuery from "components/MediaQuery";
import { useQuery } from "react-query";
import { useEffect, useMemo, useState } from "react";
import * as s from "../FilterStyled";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";
import MARKWE_ALERT from "assets/icons/map/marker_alert.svg";
import MARKWE_WARNING from "assets/icons/map/marker_warning.svg";
import MARKWE_HEALTHY from "assets/icons/map/marker_healthy.svg";
import MARKWE_CRITICAL from "assets/icons/map/marker_critical.svg";
import ONLINE_ICON from "assets/icons/online.svg";
import OFFLINE_ICON from "assets/icons/offline.svg";
import { COLORS, MAP_STATUS_COLORS } from "styles/colors";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  MapFilter,
  mapFilterInitialState,
  setStatus,
} from "redux/reducers/map/mapFilter";
import {
  DashboardFilter,
  DashboardFilterStatus,
} from "api/interfaces/dashboardInterface.interface";
import { Text, UnderlineText } from "components/atoms/text/Text";
import TinyButton from "components/atoms/buttons/TinyButton";
import { useMenu } from "hook/useMenu";
import { useAlertRules } from "hook/useAlertRules";
type Props = {
  onApply: () => void;
  onChangeFiltering: (name: string, value: boolean) => void;
};

type List = {
  label: string;
  value: "healthy" | "alert" | "critical" | "warning";
  icon: any;
};

const Wrapper = styled.ul`
  position: relative;
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 8px;

  ${mediaQuery.isMobile} {
    margin-top: 10px;
  }

  ${({ hasAlertRuleList }: { hasAlertRuleList: boolean }) =>
    !hasAlertRuleList &&
    `
      pointer-events: none;
      filter: blur(2px);
    `}
`;

const NoAlertRuleWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
  backdrop-filter: blur(2px);
  z-index: 555;
  color: ${COLORS.WHITE};
  font-weight: 500;
`;

const Item = styled.li`
  color: ${({ theme }) => theme.COLORS.WHITE};
  ${FLEX.FlexStartCenter};
  gap: 6px;
  font-size: ${calcRem(13)};

  img {
    height: 20px;
  }
`;
export const Circle = styled.p<{ bgColor: string }>`
  border-radius: 50%;
  background-color: ${({ bgColor }) => bgColor};
  border: 1px solid ${COLORS.WHITE};
  width: 15px;
  height: 15px;
`;

export const healthAlertStatus = [
  {
    label: "Online",
    value: "online",
    icon: <img src={ONLINE_ICON} alt="online icon" />,
  },
  {
    label: "Offline",
    value: "offline",
    icon: <img src={OFFLINE_ICON} alt="offline icon" />,
  },
  {
    label: "Alert",
    value: "alert",
    icon: (
      <Circle
        bgColor={MAP_STATUS_COLORS.ALERT}
        style={{ width: 10, height: 10 }}
      />
    ),

    color: MAP_STATUS_COLORS.ALERT,
  },
];

export const alertStatus = [
  {
    label: "All",
    value: "all",
    color: "#171F37",
  },
  {
    label: "Healthy",
    value: "healthy",
    color: MAP_STATUS_COLORS.HEALTHY,
  },
  {
    label: "Warning",
    value: "warning",
    color: MAP_STATUS_COLORS.WARNING,
  },
  {
    label: "Alert",
    value: "alert",
    color: MAP_STATUS_COLORS.ALERT,
  },
  {
    label: "Critical",
    value: "critical",
    color: MAP_STATUS_COLORS.CRITICAL,
  },
];

const statusList: List[] = [
  {
    label: "Healthy",
    value: "healthy",
    icon: MARKWE_HEALTHY,
  },
  {
    label: "Warning",
    value: "warning",
    icon: MARKWE_WARNING,
  },
  {
    label: "Alert",
    value: "alert",
    icon: MARKWE_ALERT,
  },
  {
    label: "Critical",
    value: "critical",
    icon: MARKWE_CRITICAL,
  },
];

const StatusFilter = (props: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const mapFilter: DashboardFilter = useAppSelector(
    (state) => state.mapFilter.filters
  );

  const { assigned, ...restFilter } = mapFilter.status;
  const exceptAssignedFilter = restFilter;

  const { menuNavigation } = useMenu({});
  const { hasAlertRuleList } = useAlertRules({});

  const [status, setStatusFilter] = useState<DashboardFilterStatus>(
    mapFilter.status
  );

  const onChange = (e: any) => {
    let {
      target: { checked, name },
    } = e;

    setStatusFilter({
      ...status,
      [name]: checked,
    });
  };

  useEffect(() => {
    if (status) {
      dispatch(setStatus(status));
    }
  }, [status]);

  useEffect(() => {
    if (exceptAssignedFilter) {
      setStatusFilter(mapFilter.status);

      if (Object.values(exceptAssignedFilter).every((status) => !status)) {
        props.onChangeFiltering("status", false);
      } else {
        props.onChangeFiltering("status", true);
      }
    }
  }, [exceptAssignedFilter]);

  const onResetFilter = () => {
    dispatch(setStatus(mapFilterInitialState.filters.status));
    setStatusFilter(mapFilterInitialState.filters.status);
  };

  return (
    <s.Container>
      <mediaQuery.Mobile>
        <s.Title>
          <Text fontSize={14} bold>
            {hasAlertRuleList ? "Status" : "No Alert Rule"}
          </Text>
        </s.Title>
      </mediaQuery.Mobile>
      {!hasAlertRuleList && (
        <NoAlertRuleWrapper>
          No Alert Rule
          <TinyButton
            label="Create Alert Rules"
            onClickBtn={() =>
              menuNavigation({
                to: `/alert/rules`,
              })
            }
            data-testid="dashboard-map-status-tiny-button-menu-navigation"
          />
        </NoAlertRuleWrapper>
      )}

      <Wrapper hasAlertRuleList={hasAlertRuleList}>
        {statusList.map((stat: List) => {
          return (
            <Item key={stat.label}>
              <label>
                <input
                  type="checkbox"
                  checked={status[stat.value]}
                  name={stat.value}
                  onChange={onChange}
                  data-testid="dashboard-map-status-input-change"
                />

                <img src={stat.icon} alt={`${stat.label} icon`} />
                {stat.label}
              </label>
            </Item>
          );
        })}
      </Wrapper>
      <mediaQuery.Default>
        <s.Reset>
          <UnderlineText
            color={COLORS.LIGHTGRAY}
            fontSize={12}
            onClick={onResetFilter}
            data-testid="dashboard-map-status-underline-text-reset-filter"
          >
            Clear
          </UnderlineText>
          <TinyButton
            label="Done"
            onClickBtn={props.onApply}
            data-testid="dashboard-map-status-tiny-button-apply"
          />
        </s.Reset>
      </mediaQuery.Default>
    </s.Container>
  );
};

export default StatusFilter;
